/**
 * Abstraction for token storage so web can use localStorage/cookie and mobile AsyncStorage/SecureStore.
 */
export interface TokenStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

const TOKEN_KEY = "token";
const CUSTOMER_TOKEN_KEY = "book4_customer_token";
const CUSTOMER_TOKEN_COOKIE = "customer_token";
const TOKEN_COOKIE = "token";
/** Used during Business → Customer switch */
export const SWITCH_CUSTOMER_TOKEN_KEY = "book4_switch_customer_token";

function getCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/** Default storage for browser: localStorage + document.cookie for server-auth compatibility */
export const browserTokenStorage: TokenStorage = {
  async getItem(key: string): Promise<string | null> {
    if (key === TOKEN_KEY) {
      const fromStorage = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
      const fromCookie = getCookieValue(TOKEN_COOKIE);
      return fromStorage ?? fromCookie ?? null;
    }
    if (key === CUSTOMER_TOKEN_KEY) {
      const fromSwitch =
        typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem(SWITCH_CUSTOMER_TOKEN_KEY)
          : null;
      if (fromSwitch) return fromSwitch;
      const fromStorage =
        typeof localStorage !== "undefined" ? localStorage.getItem(CUSTOMER_TOKEN_KEY) : null;
      const fromCookie = getCookieValue(CUSTOMER_TOKEN_COOKIE);
      return fromStorage ?? fromCookie ?? null;
    }
    return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  },

  async setItem(key: string, value: string): Promise<void> {
    if (typeof localStorage === "undefined") return;
    if (key === "token") {
      localStorage.setItem("token", value);
      document.cookie = `token=${value}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
      return;
    }
    if (key === CUSTOMER_TOKEN_KEY) {
      localStorage.setItem(CUSTOMER_TOKEN_KEY, value);
      document.cookie = `${CUSTOMER_TOKEN_COOKIE}=${value}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
      return;
    }
    localStorage.setItem(key, value);
  },

  async removeItem(key: string): Promise<void> {
    if (typeof localStorage === "undefined") return;
    if (key === "token") {
      localStorage.removeItem("token");
      document.cookie = "token=; path=/; max-age=0";
      return;
    }
    if (key === CUSTOMER_TOKEN_KEY) {
      localStorage.removeItem(CUSTOMER_TOKEN_KEY);
      document.cookie = `${CUSTOMER_TOKEN_COOKIE}=; path=/; max-age=0`;
      return;
    }
    localStorage.removeItem(key);
  },
};
